import React, {
  Dispatch, SetStateAction, useEffect, useState,
} from 'react';

import DigitContainer from '../DigitContainer';
import { DIGITS_CONTAINER_DATA } from '../../flows/login/constants';

const DIRECTIONS = {
  FORWARD: 'forward',
  BACKWARD: 'backward',
};
const EMPTY_DIGIT = '';

export enum DigitsContainerInputType {
  LETTERS,
  NUMBERS,
  NUMBER_AND_LETTERS,
}

interface IProps {
  numOfDigits: number;
  onFulfill: (answer: string) => void;
  onDigitsChanged?: (answer: string) => void;
  isError: boolean;
  isLoading: boolean;
  containerClassName?: string;
  valueToValidate?: string;
  otpManualRequest?: {
    isManualOtpSent: boolean;
    setIsManualOtpSent: Dispatch<SetStateAction<boolean>>;
  };
  resetIsError?: () => void;
  needCleanParentState?: boolean;
  inputType?: DigitsContainerInputType;
  focusOnStart?: boolean;
}

const DigitsContainer = ({
  numOfDigits,
  onFulfill,
  onDigitsChanged,
  isError,
  isLoading,
  containerClassName,
  otpManualRequest,
  valueToValidate,
  resetIsError,
  needCleanParentState,
  inputType = DigitsContainerInputType.NUMBERS,
  focusOnStart = true,
}: IProps) => {
  const [digits, setDigits] = useState<string[]>(
    Array(numOfDigits).fill(EMPTY_DIGIT),
  );
  const [requestSent, setRequestSent] = useState(false);
  const [currentDigit, setCurrentDigit] = useState(focusOnStart ? 0 : -1);

  const { CONTAINER, DIGIT_CONTAINER, KEYBOARD_EVENT_DIGITS } = DIGITS_CONTAINER_DATA;

  const isInsertedValueValid = (key: string) => KEYBOARD_EVENT_DIGITS.filter((digit) => digit === key).length === 1;
  const isDigitsArrayFull = (state: string[]) => state.filter((digit) => digit === EMPTY_DIGIT).length === 0;
  const changeDigit = (direction: string) => setCurrentDigit((prevIndex) => (direction === DIRECTIONS.BACKWARD ? prevIndex - 1 : prevIndex + 1));

  const handleDigitsChange = (index: number, value: string) => {
    if (resetIsError) resetIsError();
    const prevState = [...digits];
    prevState[index] = value;
    setDigits(prevState);
    if (onDigitsChanged) onDigitsChanged(prevState.join(''));
    return prevState;
  };

  const getRegexByInputType = () => {
    switch (inputType) {
      case DigitsContainerInputType.LETTERS:
        return /[a-zA-Z]/g;
      case DigitsContainerInputType.NUMBERS:
        return /[0-9]/g;
      case DigitsContainerInputType.NUMBER_AND_LETTERS:
        return /[a-zA-Z0-9]/g;
      default:
        return /[0-9]/g;
    }
  };

  const handleInsertDigit = (
    event: React.ChangeEvent<HTMLInputElement>,
    digitIndex: number,
  ): void => {
    const { currentTarget } = event;
    const { value } = currentTarget;

    if (needCleanParentState && !value) {
      onFulfill('');
      return;
    }
    const regex = getRegexByInputType();
    if (regex.test(value)) {
      const prevState = handleDigitsChange(digitIndex, value);
      changeDigit(DIRECTIONS.FORWARD);

      if (isDigitsArrayFull(prevState)) {
        onFulfill(prevState.join(''));
        setRequestSent(true);
      }
    }
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    digitIndex: number,
  ): void => {
    const { currentTarget } = event;

    if (event.key === 'Backspace' || event.key === 'Delete') {
      handleDigitsChange(digitIndex, EMPTY_DIGIT);
      changeDigit(DIRECTIONS.BACKWARD);
    } else if (event.key === 'ArrowLeft' && currentTarget.previousSibling) {
      changeDigit(DIRECTIONS.BACKWARD);
    } else if (event.key === 'ArrowRight' && currentTarget.nextSibling) {
      changeDigit(DIRECTIONS.FORWARD);
    } else if (
      event.currentTarget.value !== EMPTY_DIGIT
      && isInsertedValueValid(event.key)
      && currentTarget.nextSibling
    ) {
      changeDigit(DIRECTIONS.FORWARD);
    }
  };

  useEffect(() => {
    if (otpManualRequest && valueToValidate) {
      const { isManualOtpSent, setIsManualOtpSent } = otpManualRequest;
      if (isDigitsArrayFull(digits)) {
        if (valueToValidate === digits.join('')) {
          setRequestSent(false);
          setIsManualOtpSent(false);
        } else {
          if (!requestSent) {
            setRequestSent(true);
          }
          if (!isManualOtpSent) {
            setIsManualOtpSent(true);
          }
        }
      } else {
        setIsManualOtpSent(false);
      }
    }
  }, [digits]);

  return (
    <div className={containerClassName || CONTAINER.CLASS_NAME}>
      {digits.map((item, index) => (
        <DigitContainer
          id={`digitContainer${index}`}
          value={item}
          digitIndex={index}
          key={`${item}-${1 + index}`}
          handleInsertDigit={handleInsertDigit}
          handleRemoveDigit={handleKeyDown}
          isFocus={index === currentDigit}
          onClick={() => setCurrentDigit(index)}
          className={DIGIT_CONTAINER.CLASS_NAME}
          validationFailed={isError}
          isLoading={isLoading}
          otpRequestSent={{ requestSent, setRequestSent }}
        />
      ))}
    </div>
  );
};

export default DigitsContainer;
